import React from 'react'
import { Card, CardContent, CardHeader, CardTitle } from './ui/card'
import { Button } from './ui/button'
import { CheckCircleIcon, ChevronRightIcon } from '@heroicons/react/24/outline'
import { Package } from 'lucide-react'

const PricingSidebar = ({
  activeCompany,
  customerInfo,
  selectedServices,
  calculatePricing,
  companyColors,
  discount,
  canAccessTab,
  onContinue,
}) => {
  const fmt = (v) => v.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

  const renderServiceLine = (service) => {
    let effectivePrice = service.price || service.originalPrice
    let selectedAltTitle = null
    if (service.alternatives?.length > 0 && service.selectedAlternativeId) {
      const selectedAlt = service.alternatives.find(a => a.id === service.selectedAlternativeId)
      if (selectedAlt) { effectivePrice = selectedAlt.price; selectedAltTitle = selectedAlt.title }
    }
    const qty = service.quantity || 1
    let tieredUnitPrice = effectivePrice
    if (service.priceTiers?.length > 0) {
      const tier = service.priceTiers.find(t => t.upTo === null || qty <= t.upTo) || service.priceTiers[service.priceTiers.length - 1]
      tieredUnitPrice = tier.pricePerUnit ?? effectivePrice
    }
    const basePrice = tieredUnitPrice * qty
    const serviceDiscount = service.customDiscount?.value > 0
      ? (service.customDiscount.type === 'percentage' ? basePrice * (service.customDiscount.value / 100) : service.customDiscount.value) : 0
    const finalPrice = basePrice - serviceDiscount

    return (
      <div key={service.id} className="ml-5 space-y-1">
        <div className="flex justify-between text-sm">
          <div className="flex items-center gap-2">
            {service.fromPackage && <Package className="w-3 h-3 text-blue-600" />}
            <span className="font-medium text-slate-700">{service.name}</span>
            {selectedAltTitle && <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">{selectedAltTitle}</span>}
          </div>
          <div className="font-medium text-slate-800">€{fmt(finalPrice)}</div>
        </div>
        <div className="flex justify-between text-xs text-slate-500">
          <div>
            {service.fromPackage && <span className="text-blue-600 mr-2">📦 {service.packageName}</span>}
            {service.type === 'einmalig'
              ? (service.quantity > 1 ? `${service.quantity}x €${fmt(tieredUnitPrice)}${service.priceTiers?.length > 0 ? ' (Staffel)' : ''}` : 'Einmalig')
              : <span style={{ color: service.type === 'monatlich' ? companyColors.monatlich : companyColors.jaehrlich }}>
                  {service.type === 'monatlich' ? 'Monatlich' : 'Jährlich'}
                </span>}
            {service.customDiscount?.value > 0 && (
              <span className="ml-2 text-green-600">(-{service.customDiscount.value}{service.customDiscount.type === 'percentage' ? '%' : '€'})</span>
            )}
            {service.isOptional && <span className="ml-2 text-orange-600">(Optional - nicht enthalten)</span>}
          </div>
        </div>
        {service.notes && <div className="text-xs text-blue-600 italic">💬 {service.notes}</div>}
      </div>
    )
  }

  const serviceValues = Object.values(selectedServices)
  const einmaligServices = serviceValues.filter(s => s.type === 'einmalig')
  const laufendServices = serviceValues.filter(s => s.type === 'monatlich' || s.type === 'jährlich' || s.type === 'variabel')

  return (
    <div className="lg:col-span-3 space-y-6">
      <Card className="sticky top-6 flex flex-col" style={{ maxHeight: 'calc(100vh - 100px)' }}>
        <CardHeader className="flex-shrink-0">
          <CardTitle className="text-slate-800">Kostenübersicht</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 overflow-y-auto flex-grow" style={{ maxHeight: 'calc(100vh - 350px)' }}>
          {activeCompany && (
            <div className="pb-4 border-b border-slate-200">
              <div className="text-sm text-slate-600"><strong>{activeCompany.name}</strong></div>
            </div>
          )}
          {customerInfo.company && (
            <div className="pb-4 border-b border-slate-200">
              <div className="text-sm text-slate-600"><strong>{customerInfo.company}</strong></div>
              <div className="text-xs text-slate-500">{customerInfo.name}</div>
            </div>
          )}

          {serviceValues.length > 0 && (
            <div className="space-y-4">
              <h3 className="font-medium text-slate-800">Ausgewählte Services</h3>

              {/* Einmalige Kosten */}
              {einmaligServices.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: companyColors.einmalig }} />
                    <h4 className="font-medium" style={{ color: companyColors.einmalig }}>Einmalige Kosten</h4>
                  </div>
                  {einmaligServices.map(renderServiceLine)}
                  <div className="ml-5 pt-2 border-t border-slate-200">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Zwischensumme Einmalig</span>
                      <span>€{fmt(calculatePricing.einmaligSubtotal)}</span>
                    </div>
                    {calculatePricing.einmaligDiscount > 0 && (
                      <div className="flex justify-between text-sm text-green-600">
                        <span>Rabatt ({discount.oneTime.type === 'percentage' ? `${discount.oneTime.value}%` : `€${discount.oneTime.value}`})</span>
                        <span>-€{fmt(calculatePricing.einmaligDiscount)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm font-bold" style={{ color: companyColors.einmalig }}>
                      <span>Summe Einmalig</span>
                      <span>€{fmt(calculatePricing.einmaligTotal)}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Laufende Kosten */}
              {laufendServices.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: companyColors.monatlich }} />
                    <h4 className="font-medium" style={{ color: companyColors.monatlich }}>Laufende Kosten</h4>
                  </div>
                  {laufendServices.map(renderServiceLine)}
                  <div className="ml-5 pt-2 border-t border-slate-200">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Zwischensumme Laufend</span>
                      <span>€{fmt(calculatePricing.laufendSubtotal)}</span>
                    </div>
                    {calculatePricing.laufendDiscount > 0 && (
                      <div className="flex justify-between text-sm" style={{ color: companyColors.monatlich }}>
                        <span>Rabatt ({discount.monthly.type === 'percentage' ? `${discount.monthly.value}%` : `€${discount.monthly.value}`})</span>
                        <span>-€{fmt(calculatePricing.laufendDiscount)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm font-bold" style={{ color: companyColors.monatlich }}>
                      <span>Summe Laufend</span>
                      <span>€{fmt(calculatePricing.laufendTotal)}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Optionale Services */}
              {calculatePricing.optionalServices?.length > 0 && (
                <div className="space-y-2 border-t border-slate-200 pt-2">
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-gray-400 rounded-full" />
                    <h4 className="font-medium text-gray-600">Optional Services</h4>
                    <span className="text-xs text-orange-600">(nicht im Endpreis enthalten)</span>
                  </div>
                  {calculatePricing.optionalServices.map(service => {
                    const basePrice = (service.price || service.originalPrice) * (service.quantity || 1)
                    const sd = service.customDiscount?.value > 0
                      ? (service.customDiscount.type === 'percentage' ? basePrice * (service.customDiscount.value / 100) : service.customDiscount.value) : 0
                    return (
                      <div key={service.id} className="ml-5 space-y-1">
                        <div className="flex justify-between text-sm text-gray-600">
                          <div>{service.name} <span className="text-gray-400">(€{fmt(basePrice - sd)})</span></div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )}
        </CardContent>

        {/* Gesamtsumme */}
        <div className="flex-shrink-0 px-6 pb-4 pt-2 border-t border-slate-200 bg-white">
          <div className="space-y-2">
            {calculatePricing.einmaligTotal > 0 && (
              <div className="flex justify-between text-sm font-semibold" style={{ color: companyColors.einmalig }}>
                <span>Einmalig</span><span>€{fmt(calculatePricing.einmaligTotal)}</span>
              </div>
            )}
            {calculatePricing.monatlichTotal > 0 && (
              <div className="flex justify-between text-sm font-semibold" style={{ color: companyColors.monatlich }}>
                <span>Monatlich</span><span>€{fmt(calculatePricing.monatlichTotal)} / M</span>
              </div>
            )}
            {calculatePricing.jaehrlichTotal > 0 && (
              <div className="flex justify-between text-sm font-semibold" style={{ color: companyColors.jaehrlich }}>
                <span>Jährlich</span><span>€{fmt(calculatePricing.jaehrlichTotal)} / J</span>
              </div>
            )}
            <div className="border-t-2 border-slate-300 pt-2 mt-2">
              <div className="flex justify-between text-base font-bold text-slate-800">
                <span>Gesamt</span><span>€{fmt(calculatePricing.total)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Weiter Button */}
        <div className="px-6 pb-6 space-y-3">
          <div className="p-4 bg-green-50 rounded-lg border border-green-200">
            <div className="flex items-center text-green-800">
              <CheckCircleIcon className="h-5 w-5 mr-2" />
              <span className="font-medium">Services ausgewählt: {serviceValues.length} Position{serviceValues.length !== 1 ? 'en' : ''}</span>
            </div>
          </div>
          <Button onClick={onContinue} disabled={!canAccessTab('pdf')} className="w-full bg-blue-600 hover:bg-blue-700 text-white">
            Weiter <ChevronRightIcon className="h-4 w-4 ml-2" />
          </Button>
        </div>
      </Card>
    </div>
  )
}

export default PricingSidebar
